import React from 'react';
import { DataTable } from 'primereact/components/datatable/DataTable';
import { Column } from 'primereact/components/column/Column';
import { Button } from 'primereact/components/button/Button';
import moment from 'moment';

import CountryDataTable from '../campaign/countryDataTable';

import clientService from '../../../../../../utils/services/client';
import config from '../../../../../../config/config';
import ShieldIcon from '../../../../../shared/shieldIcon/ShieldIcon';
import PartnerBadge from '../../../../../shared/partnerBadge/PartnerBadge';

class ClientDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      list: [],
      expandedRows: [],
      searchField: this.props.searchField,
      ignoreArchived: this.props.ignoreArchived,
      thirdPartyOnly: this.props.thirdPartyOnly,
      first: 0,
      // Per-session, per-client confirmation state for the partner-guard overlay.
      // Not persisted, not Redux - resets on unmount/page reload as intended.
      confirmedPartnerClientIds: new Set()
    };
  }

  getQuery = e => {
    const query = {
      $offset: e.first,
      $limit: e.rows
    };
    if (this.state.searchField) query.$search = this.state.searchField;
    if (this.state.thirdPartyOnly) query.partnerId_$notNull = true;

    return query;
  }

  getExtendedQuery = e => {
    var extendedQuery = '?';
    if (e.multiSortMeta != null && e.multiSortMeta.length > 0) {
      for (let sortMeta of e.multiSortMeta) {
        var sortField = `$sort=${sortMeta.order == 1 ? '' : '-'}${sortMeta.field}&`;
        extendedQuery = extendedQuery + sortField;
      }
    }
    return extendedQuery;
  }

  setSearch = (search) => {
    const { searchField, ignoreArchived, campaignType, thirdPartyOnly } = search;
    this.setState({ searchField: searchField, ignoreArchived: ignoreArchived, campaignType, thirdPartyOnly, first: 0 }, () => this.onLazyLoad({}));
  }

  editAction = rowData => <span className="fa fa-cog" style={{ cursor: 'pointer' }} onClick={() => {
    window.open(`${config.URL.BASEFRONT}/admin/client/${rowData._id}`, '_blank');
  }} />
  
  openGlobalVarIcon = rowData => <span className="fa fa-sliders" style={{ cursor: 'pointer' }} onClick={() => {
    window.open(`${config.URL.BASEFRONT}/admin/client/${rowData._id}/configuration`, '_blank');
  }} />

  openCountryCode = rowData => <span className="fa fa-map-signs" style={{ cursor: 'pointer' }} onClick={() => {
    window.open(`${config.URL.BASEFRONT}/admin/countryCode?clientId=${rowData._id}`, '_blank');
  }} />

  confirmPartnerClient = clientId => {
    this.setState(prevState => {
      const confirmedPartnerClientIds = new Set(prevState.confirmedPartnerClientIds);
      confirmedPartnerClientIds.add(clientId);
      return { confirmedPartnerClientIds };
    });
  }

  // Collapses just this row back up, rather than leaving "keep browsing elsewhere" as
  // the only way out of the guard.
  cancelPartnerGuard = clientId => {
    this.setState(prevState => ({
      expandedRows: prevState.expandedRows.filter(row => row._id !== clientId)
    }));
  }

  // Rendered in place of CountryDataTable (never alongside it - see rowExpansionTemplate
  // below), so this is just the expanded row's content, not an overlay stacked on top of
  // anything. It used to be position:absolute over a fixed-height wrapper, which made
  // sense only if real content sat underneath it; since nothing ever does, that box could
  // be shorter than the card's own content and the card would bleed into the row above/
  // below it uncontained. Plain flow content sizes itself to what it actually holds.
  renderPartnerGuardOverlay = data =>
    <div style={{
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: 420,
        width: '100%',
        border: '1px solid #e7c88c',
        backgroundColor: '#fbf1e2',
        borderRadius: 10,
        padding: '16px 18px'
      }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <ShieldIcon size={18} color="#a3670f" style={{ marginTop: 2 }} />
          <div>
            <h3 style={{ fontSize: '14.5px', margin: '0 0 3px', color: '#292B2C' }}>Partner-created client</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#777777' }}>
              This client&apos;s campaigns were set up through a partner integration. Changes made here
              won&apos;t sync back to the partner &mdash; make sure that&apos;s what you intend.
            </p>
            <span style={{
              display: 'inline-block',
              marginTop: 8,
              fontSize: '11.5px',
              padding: '2px 7px',
              borderRadius: 5,
              backgroundColor: '#ffffff',
              border: '1px solid #dddddd',
              color: '#777777'
            }}>
              partnerId: {data.partnerId}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <Button label="Cancel" className="ui-button-secondary" onClick={() => this.cancelPartnerGuard(data._id)} />
          <Button label="View campaigns anyway" onClick={() => this.confirmPartnerClient(data._id)} />
        </div>
      </div>
    </div>

  rowExpansionTemplate = data => {
    // There is no permanent bypass for this guard (per PR review on STP-168's backend
    // half: no partnerGuardExempt column, rely only on partnerId) - every partner-created
    // client requires a fresh per-session click-through every time, so whoever is in
    // front of the screen right now always consciously confirms intent.
    const isPartnerCreated = !!data.partnerId;
    const needsConfirmation = isPartnerCreated && !this.state.confirmedPartnerClientIds.has(data._id);

    if (!needsConfirmation) {
      return <CountryDataTable clientId={data._id} ignoreArchived={this.state.ignoreArchived}/>;
    }

    return this.renderPartnerGuardOverlay(data);
  }

  onLazyLoad = (e) => {
    this.state.first = isNaN(e.first) ? 0 : e.first;

    this.setState({ loading: true});

    clientService.getClientPage(this.getQuery(e), this.getExtendedQuery(e))
      .then(response => this.setState({
        list: response.data.page.map(this.format),
        totalRecords: response.data.totalCount,
        loading: false
      }))
      .then(() => {
        if (this.state.list.length === 1) {
          this.setState({ expandedRows: [this.state.list[0]] });
        }
      });
  }

  statusTemplate = (rowData) => {

    return (<i class="fa fa-circle" style ={{ color: (rowData.active) ? 'green' : 'red' }} aria-hidden="true"></i>);
  }

  partnerBadge = rowData =>
    !!rowData.partnerId && <PartnerBadge />

  format = client => ({
    ...client,
    createdAt: moment(client.createdAt).format('YYYY-MM-DD'),
    updatedAt: moment(client.updatedAt).format('YYYY-MM-DD'),

  });

  render = () =>
    <DataTable
      value={this.state.list}
      responsive={true}
      sortMode="multiple"
      paginator={true} rows={10}
      rowsPerPageOptions={[5, 10, 20, 100, 500, 1000]}
      totalRecords={this.state.totalRecords}
      lazy={true}
      onLazyLoad={this.onLazyLoad}
      loading={this.state.loading}
      ref={(el) => this.dt = el}
      onRowToggle={e => this.setState({ expandedRows: e.data })}
      rowExpansionTemplate={(e) => this.rowExpansionTemplate(e)}
      expandedRows={this.state.expandedRows}
      first = {this.state.first}

    >
      <Column expander={true} style={{ width: '45px' }} />
      <Column field="_id" header="ID" />
      <Column field="name" header="Client" sortable={true} />
      <Column body={this.partnerBadge} header="3rd Party" style={{ width: '160px' }} />
      <Column field="countryName" header="Country" sortable={true} style={{ width: '160px' }} />
      <Column field="createdAt" header="Created" sortable={true} />
      <Column field="updatedAt" header="Updated" sortable={true} />
      <Column field="active" header="Active" body={this.statusTemplate} style={{ width: '60px' }} sortable={true} />
      <Column body={this.openCountryCode} style={{ textAlign: 'center', width: '50px' }} />
      <Column body={this.openGlobalVarIcon} style={{ textAlign: 'center', width: '50px' }} />
      <Column body={this.editAction} style={{ textAlign: 'center', width: '50px' }} />
    </DataTable>
}

export default ClientDataTable;